import { Routes, type Params, type UrlSegment } from '@angular/router';
import { inicioGuard, moduloGuard } from '@intelishs-group/session-core';
import { Marco } from '@components/marco/marco';
import {
  RUTA_CLIENTES,
  RUTA_EMPRESAS,
  RUTA_NOMINAS,
  SECCIONES_NIVEL,
  aConfiguracion,
  aRuta,
  conConsulta,
  soloGlobalGuard,
  type PantallaConBarra,
} from '@services/espacio.service';
import { RUTA_TRABAJADORES } from '@services/navegacion.service';

/**
 * Cada area con lo suyo (fnomina `cfbed6f`, 2026-10-12) y, desde el 2026-10-22, cada area es un MODULO de
 * la aplicacion `nomina` en identidad: el menu del marco de SINAY son los modulos que identidad otorga al
 * rol activo, y cada area cuelga del prefijo de su modulo y se protege con `moduloGuard('<codigo>')`:
 *
 *   modulo              ruta del area          lo que cuelga de ella
 *   inicio              /inicio                (la portada: las areas y como funciona)
 *   personal            /personal              /personal/trabajadores/**
 *   nomina              /nomina                /nomina/**
 *   reportes            /reportes              /reportes/**
 *   empresas-clientes   /empresas-clientes     /empresas-clientes/{empresas,clientes,nominas}/**
 *   configuracion       /configuracion         /configuracion/** (y la consulta de parametros)
 *
 * Cada area tiene su pagina en su ruta (`aterrizaje`: la del inicio con `data.area`, con las entradas del
 * area). Una ruta nueva va SIEMPRE debajo de un modulo: fuera de ellos solo quedan `/demo` y las
 * redirecciones de antes.
 *
 * Al entrar (`''`) y en lo que no existe (`'**'`), `inicioGuard` lleva a la PRIMERA pantalla del acceso: el
 * primer modulo por su `orden` en identidad (hoy `inicio`, orden 1), como ffacturacion y fjuridico. Sin el
 * modulo de una ruta, `moduloGuard` manda a `/` y de ahi `inicioGuard` decide; sin ningun modulo, al login
 * central. Por eso no hay `rutaSinPermiso`: antes `/inicio` era su destino fijo e iba sin guarda.
 *
 *   /configuracion          Configuracion: como se calcula. Primero la pantalla y despues el nivel:
 *                           /configuracion/<seccion> (GENERAL), .../esquemas/:esquema (ESQUEMA),
 *                           .../empresas/:empresa (EMPRESA), .../clientes/:cliente (CLIENTE_EMPRESA),
 *                           .../nominas/:tipo (TIPO_NOMINA; `:tipo` es el id de la nomina) y
 *                           /configuracion/<seccion>/clientes/:cliente (CLIENTE). El nivel se elige en
 *                           la barra «¿A qué aplica?». El calendario, solo en una nomina; bases,
 *                           parametros, esquemas de pago y listas del trabajador, solo en la general
 *                           (solo el global).
 *   /empresas-clientes/empresas/:empresa   Empresas y clientes: datos, registros, estructura, turnos y
 *                           sindicatos, sus clientes y, de cada uno, sus nominas (alta y cambio)
 *   /empresas-clientes/clientes/:cliente   datos, razones sociales, facturadoras y empresas donde esta su personal
 *
 * Fuera de los espacios, `/configuracion/consulta-parametros` es la consulta de los parametros del
 * ejercicio para cualquier usuario (solo lectura: se cambian en
 * `/configuracion/parametros`), `/empresas-clientes/nominas` (2026-10-09) todas las nominas del alcance y
 * `/personal/trabajadores` el padron (Personal, traido el 2026-10-15; desde fnomina `5f6bfe0` con «Altas desde
 * archivo» en `.../altas-archivo`, «Cambios masivos» en `.../cambios-masivos`, «Avisos al IMSS» en
 * `.../avisos-imss` y «Modificaciones del bimestre» en `.../bimestre-imss`) y `/nomina/incidencias` (2026-10-13;
 * traida el 2026-10-20) las incidencias del periodo (Nomina), con su carga masiva en `/nomina/incidencias/carga`, y
 * `/nomina/faltas-incapacidades` (etapa I2) las faltas e incapacidades del periodo en operacion, con su
 * historico en `/nomina/faltas-incapacidades/historico` (2026-10-14; `/anteriores` redirige ahi), y
 * `/nomina/deducciones-fijas` (etapa I3, 2026-10-14) los prestamos y deducciones fijas y la consulta de creditos y
 * pension, y `/reportes/nomina-calculada` (2026-10-16) el Excel de la nomina calculada (area Reportes).
 *
 * En SINAY no se porta el area Administracion (usuarios y perfiles: los orquesta
 * SINAY con adminportal, decision D19); por eso no hay `/general/usuarios|perfiles`.
 * Tampoco los permisos finos de fnomina (`permisoGuard(P.…)`): el portal distingue
 * solo con `configuracion-general` (`soloGlobalGuard`); lo demas lo ve toda sesion y cada
 * pantalla habilita la escritura con la funcion de su accion (`PermisosService`).
 *
 * En SINAY los ids de la ruta son los de clientesapi: `:empresa` = `empresa_id` de
 * la empresa (`kidempresa`: la pagadora o, si solo factura, la facturadora),
 * `:cliente` = id del cliente comercial y `:esquema` = id del esquema de pago.
 * `:tipo` es el id de la nomina (de nominaapi). Los grupos de clientes son una
 * ventana de la lista de clientes: `.../clientes/grupos` (y `/grupos`) llevan a ella.
 *
 * El nivel sale de la ruta: `data.nivel` y los parametros (`:esquema`, `:empresa`,
 * `:cliente`, `:tipo`) los lee `EspacioService` y fija `NivelService`. Con
 * `paramsInheritanceStrategy: 'always'` (en `app.config.ts`) la hoja hereda los
 * parametros y datos de sus padres. `data.seccion` cierra las migas de pan,
 * `data.config` dice que la seccion configura el nivel (avisa si es de solo
 * lectura) y `data.barra` + `data.pantalla`, que lleva la barra de Configuracion.
 *
 * Las rutas de antes (`/general/conceptos`, `/empresas/1/conceptos`,
 * `.../nominas/:tipo/calendario`…) redirigen a su lugar nuevo con los mismos ids, y las de antes del
 * 2026-10-22 sin el prefijo de su area (`/trabajadores/...`, `/empresas/...`, `/clientes/...`,
 * `/nominas`, `/parametros`), a la misma ruta debajo de el (`deAntes`).
 */

const espacio = () => import('./pages/espacio/espacio.page').then((m) => m.EspacioPage);
/** La pagina de cada area: la del inicio con `data.area` (las entradas de esa area). */
const aterrizaje = () => import('./pages/inicio/inicio.page').then((m) => m.InicioPage);
const conceptos = () => import('./pages/conceptos/conceptos.page').then((m) => m.ConceptosPage);
const variables = () => import('./pages/variables/variables.page').then((m) => m.VariablesPage);
const prestaciones = () => import('./pages/prestaciones/prestaciones.page').then((m) => m.PrestacionesPage);
const reglaLugar = () => import('./pages/regla-lugar/regla-lugar.page').then((m) => m.ReglaLugarPage);
const empresas = () => import('./pages/empresas/empresas.page').then((m) => m.EmpresasPage);
const clientes = () => import('./pages/clientes/clientes.page').then((m) => m.ClientesPage);
const calendario = () => import('./pages/calendario/calendario.page').then((m) => m.CalendarioPage);
const parametros = () => import('./pages/parametros/parametros.page').then((m) => m.ParametrosPage);
const nominasClienteEmpresa = () =>
  import('./pages/nominas-cliente-empresa/nominas-cliente-empresa.page').then((m) => m.NominasClienteEmpresaPage);
const camposTrabajador = () =>
  import('./pages/campos-trabajador/campos-trabajador.page').then((m) => m.CamposTrabajadorPage);
const importaciones = () => import('./pages/importaciones/importaciones.page').then((m) => m.ImportacionesPage);
const importacion = () => import('./pages/importacion/importacion.page').then((m) => m.ImportacionPage);
const importarTrabajadores = () =>
  import('./pages/importar-trabajadores/importar-trabajadores.page').then((m) => m.ImportarTrabajadoresPage);

type Ruta = Routes[number];
type Carga = NonNullable<Ruta['loadComponent']>;

/**
 * Una pantalla de Configuracion con la barra «¿A qué aplica?»: la pantalla y, debajo, cada nivel con
 * los mismos parametros de siempre (las paginas leen el nivel de `NivelService`, como antes). En SINAY
 * entra cualquier sesion: la consulta ve todos los niveles en solo lectura y la general, solo el
 * global (la barra no le ofrece «Todas» y la pantalla espera a que elija).
 */
function pantallaConBarra(pantalla: PantallaConBarra, seccion: string, carga: Carga): Ruta {
  const data = { seccion, config: true, barra: true, pantalla };
  return {
    path: pantalla,
    children: [
      { path: '', data: { ...data, nivel: 'GENERAL' }, loadComponent: carga },
      { path: 'esquemas/:esquema', data: { ...data, nivel: 'ESQUEMA' }, loadComponent: carga },
      { path: 'empresas/:empresa', data: { ...data, nivel: 'EMPRESA' }, loadComponent: carga },
      { path: 'empresas/:empresa/clientes/:cliente', data: { ...data, nivel: 'CLIENTE_EMPRESA' }, loadComponent: carga },
      {
        path: 'empresas/:empresa/clientes/:cliente/nominas/:tipo',
        data: { ...data, nivel: 'TIPO_NOMINA' },
        loadComponent: carga,
      },
      { path: 'clientes/:cliente', data: { ...data, nivel: 'CLIENTE' }, loadComponent: carga },
    ],
  };
}

/**
 * Las secciones de configuracion de un nivel en las rutas de antes (`/general/conceptos`,
 * `/empresas/1/variables`…): redirigen a la misma seccion de Configuracion en ese nivel.
 */
function configuracionDeAntes(): Routes {
  return SECCIONES_NIVEL.map((s) => ({ path: s, redirectTo: aConfiguracion(s) }));
}

/** Las rutas de antes, con `?empresa=`, llevan a esa empresa del espacio Empresas. */
function aLaEmpresa(seccion: string) {
  return ({ queryParams }: { queryParams: Record<string, string> }) => {
    const id = Number(queryParams['empresa']);
    return id ? `${RUTA_EMPRESAS}/${id}/${seccion}` : RUTA_EMPRESAS;
  };
}

/**
 * Las rutas de antes del 2026-10-22, sin el prefijo de su area (`/trabajadores/5`, `/empresas/7/datos`…):
 * la MISMA ruta debajo de `destino`, con su consulta. Lo que ya redirigia sigue redirigiendo desde ahi.
 */
function deAntes(destino: string): Routes {
  const a = ({ url, queryParams }: { url: UrlSegment[]; queryParams: Params }): string =>
    conConsulta([destino, ...url.map((s) => s.path)].join('/'), queryParams);
  return [
    { path: '', pathMatch: 'full', redirectTo: a },
    { path: '**', redirectTo: a },
  ];
}

/**
 * `/empresas/:empresa/<seccion>` que ya no es de Empresas y clientes: la lista de su area con
 * `?empresa=`, conservando los demas parametros de consulta.
 */
function conLaEmpresa(destino: string) {
  return ({ params, queryParams }: { params: Record<string, string>; queryParams: Record<string, string> }) => {
    const id = Number(params['empresa']);
    return conConsulta(destino, queryParams, id ? { empresa: String(id) } : {});
  };
}

/**
 * Las pantallas de Nomina preguntan antes de salir si queda algo sin guardar (fnomina bd4687a).
 * SINAY: si la pantalla no llego a crearse (`null`), deja salir; en fnomina el guard la llamaba sin revisar.
 */
const preguntarAlSalir = (pagina: { puedeSalir(): boolean | Promise<boolean> } | null): boolean | Promise<boolean> =>
  pagina?.puedeSalir() ?? true;

/** Una ruta de antes con `:id`: el mismo id debajo de `destino`, conservando los parametros de consulta. */
function conId(destino: string) {
  return ({ params, queryParams }: { params: Record<string, string>; queryParams: Record<string, string> }) =>
    conConsulta(`${destino}/${params['id']}`, queryParams);
}

export const routes: Routes = [
  {
    // La RUTA PADRE con el marco de SINAY (`Marco`): el router monta el marco y la pantalla de
    // una sola vez. Sin guarda de sesion: la sesion la carga `proveerSesion` ANTES del primer
    // render y, sin una de Nomina, ya va camino al login central. Las pantallas son diferidas:
    // no las carga quien no las visita.
    path: '',
    component: Marco,
    children: [
      // Al entrar: la PRIMERA pantalla del acceso, no un destino fijo. `children: []` y no `redirectTo`:
      // una redireccion no corre guards.
      { path: '', pathMatch: 'full', canActivate: [inicioGuard()], children: [] },
      {
        path: 'inicio',
        canActivate: [moduloGuard('inicio')],
        loadComponent: () => import('./pages/inicio/inicio.page').then((m) => m.InicioPage),
      },

      // ── Configuración: cómo se calcula, en general o por esquema, empresa, cliente y nómina ──
      {
        path: 'configuracion',
        canActivate: [moduloGuard('configuracion')],
        children: [
          // La pagina del area: sus pantallas (antes `/configuracion` abria Conceptos en el ultimo nivel).
          { path: '', pathMatch: 'full', loadComponent: aterrizaje, data: { area: 'configuracion' } },
          {
            // Consulta de los parametros del ejercicio para todos (solo lectura, tambien para el global); se
            // cambian en /configuracion/parametros. Fuera del espacio: sin banda ni nivel propio. Antes `/parametros`.
            path: 'consulta-parametros',
            data: { consulta: true },
            loadComponent: parametros,
          },
          {
            path: '',
            loadComponent: espacio,
            data: { espacio: 'CONFIGURACION' },
            children: [
              pantallaConBarra('conceptos', 'Conceptos', conceptos),
              pantallaConBarra('variables', 'Variables fijas', variables),
              pantallaConBarra('prestaciones', 'Prestaciones', prestaciones),
              pantallaConBarra('regla-lugar', 'Regla del ISN y la zona', reglaLugar),
              // Lo que cada nivel pide u oculta de los campos del trabajador (2026-10-08).
              pantallaConBarra('campos-trabajador', 'Campos del trabajador', camposTrabajador),
              {
                // El calendario solo existe en una nomina: sin ella, la pantalla espera la eleccion.
                path: 'calendario',
                children: [
                  {
                    path: '',
                    data: { seccion: 'Calendario de periodos', barra: true, pantalla: 'calendario', nivel: 'GENERAL' },
                    loadComponent: calendario,
                  },
                  {
                    path: 'empresas/:empresa/clientes/:cliente/nominas/:tipo',
                    data: {
                      seccion: 'Calendario de periodos',
                      config: true,
                      barra: true,
                      pantalla: 'calendario',
                      nivel: 'TIPO_NOMINA',
                    },
                    loadComponent: calendario,
                  },
                ],
              },
              {
                // Los dias inhabiles del IMSS (fnomina 5f6bfe0, etapa IM1): solo en la general; los consulta
                // cualquiera (sin `soloGlobalGuard`) y los cambia quien tiene `configuracion-general` (en fnomina,
                // PARAMETROS_EDITAR).
                path: 'dias-inhabiles-imss',
                data: { seccion: 'Días inhábiles del IMSS', nivel: 'GENERAL', config: true },
                loadComponent: () =>
                  import('./pages/dias-inhabiles-imss/dias-inhabiles-imss.page').then((m) => m.DiasInhabilesImssPage),
              },
              {
                // Lo que solo existe en la general: con `configuracion-general`.
                path: '',
                canActivateChild: [soloGlobalGuard],
                data: { nivel: 'GENERAL' },
                children: [
                  {
                    // Las bases nombradas solo existen en la general.
                    path: 'bases',
                    data: { seccion: 'Bases nombradas', config: true },
                    loadComponent: () => import('./pages/bases/bases.page').then((m) => m.BasesPage),
                  },
                  {
                    // Los parametros del ejercicio son ley: solo existen en la general (y solo el global los cambia).
                    path: 'parametros',
                    data: { seccion: 'Parámetros del ejercicio', config: true },
                    loadComponent: parametros,
                  },
                  {
                    // La lista de esquemas de pago; la configuracion de cada uno, en la barra («Un esquema»).
                    path: 'esquemas',
                    data: { seccion: 'Esquemas de pago' },
                    loadComponent: () => import('./pages/esquemas/esquemas.page').then((m) => m.EsquemasPage),
                  },
                  {
                    // Las opciones de las listas del trabajador (2026-10-08): las cambia el administrador global.
                    path: 'listas-trabajador',
                    data: { seccion: 'Listas del trabajador', config: true },
                    loadComponent: () =>
                      import('./pages/listas-trabajador/listas-trabajador.page').then((m) => m.ListasTrabajadorPage),
                  },
                ],
              },
            ],
          },
        ],
      },

      // Rutas de antes de la configuracion general: a Configuracion (sin guarda: la pone el destino).
      {
        path: 'general',
        children: [
          { path: '', pathMatch: 'full', redirectTo: aRuta('/configuracion/conceptos') },
          ...configuracionDeAntes(),
          { path: 'bases', redirectTo: aRuta('/configuracion/bases') },
          { path: 'parametros', redirectTo: aRuta('/configuracion/parametros') },
          { path: 'listas-trabajador', redirectTo: aRuta('/configuracion/listas-trabajador') },
          { path: 'esquemas', pathMatch: 'full', redirectTo: aRuta('/configuracion/esquemas') },
          {
            path: 'esquemas/:esquema',
            children: [
              { path: '', pathMatch: 'full', redirectTo: aConfiguracion('conceptos') },
              ...configuracionDeAntes(),
            ],
          },
        ],
      },

      // ── Personal: el padron y sus movimientos ──
      {
        path: 'personal',
        canActivate: [moduloGuard('personal')],
        children: [
          { path: '', pathMatch: 'full', loadComponent: aterrizaje, data: { area: 'personal' } },
          {
            // Padron de trabajadores (Personal; fnomina 2026-10-08, en SINAY desde el 2026-10-15): dato del dia
            // a dia, fuera de los espacios. nominaapi filtra por el alcance; los datos personales se ven completos
            // para toda sesion (Juan, 2026-10-16: `TRABAJADORES_VER_SENSIBLES` sin efecto). Sin guardas de permiso fino (`permisoGuard(P.TRABAJADORES_…)` en fnomina):
            // entra quien tiene el modulo `personal` y cada pantalla habilita lo que escribe con su funcion (`trabajadores-*`).
            path: 'trabajadores',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/trabajadores/trabajadores.page').then((m) => m.TrabajadoresPage),
              },
              {
                path: 'alta',
                // Con algo capturado sin dar de alta, pregunta antes de salir (2026-10-19).
                // SINAY: sin `permisoGuard(P.TRABAJADORES_CAPTURAR)` (D19; el alta la habilita `trabajadores-capturar`).
                canDeactivate: [preguntarAlSalir],
                loadComponent: () =>
                  import('./pages/alta-trabajador/alta-trabajador.page').then((m) => m.AltaTrabajadorPage),
              },
              // Altas desde archivo y Cambios masivos (fnomina 5f6bfe0, Juan: ordenar por lo que se hace, no por el
              // medio; el reingreso va con las altas). Antes de `:relacion`. Las altas y los reingresos por archivo, en
              // `altas-archivo`; el mismo cambio a varios (movimiento masivo) y los cambios por archivo, en
              // `cambios-masivos`. SINAY: sin `permisoGuard(P.TRABAJADORES_…)` (D19): hacer uno lo habilitan
              // `trabajadores-importar`, `trabajadores-masivos` y `trabajadores-capturar` en su pantalla.
              { path: 'altas-archivo', loadComponent: importaciones },
              { path: 'altas-archivo/subir', data: { modo: 'altas' }, loadComponent: importarTrabajadores },
              { path: 'altas-archivo/:id', loadComponent: importacion },
              {
                path: 'cambios-masivos',
                loadComponent: () =>
                  import('./pages/cambios-masivos/cambios-masivos.page').then((m) => m.CambiosMasivosPage),
              },
              {
                path: 'cambios-masivos/nuevo',
                loadComponent: () =>
                  import('./pages/movimiento-masivo/movimiento-masivo.page').then((m) => m.MovimientoMasivoPage),
              },
              { path: 'cambios-masivos/archivo', data: { modo: 'cambios' }, loadComponent: importarTrabajadores },
              { path: 'cambios-masivos/archivo/:id', loadComponent: importacion },
              {
                path: 'cambios-masivos/:id',
                loadComponent: () =>
                  import('./pages/operacion-masiva/operacion-masiva.page').then((m) => m.OperacionMasivaPage),
              },
              // Las rutas de antes (hasta fnomina 5f6bfe0) llevan a su lugar nuevo con sus parametros de consulta. Una
              // importacion de antes se abre en «Altas desde archivo» y, si es de cambios, su detalle la pasa a
              // «Cambios masivos» (`replaceUrl`).
              { path: 'masivos', pathMatch: 'full', redirectTo: aRuta(`${RUTA_TRABAJADORES}/cambios-masivos`) },
              { path: 'masivo', pathMatch: 'full', redirectTo: aRuta(`${RUTA_TRABAJADORES}/cambios-masivos/nuevo`) },
              { path: 'masivo/:id', redirectTo: conId(`${RUTA_TRABAJADORES}/cambios-masivos`) },
              {
                path: 'importar',
                pathMatch: 'full',
                redirectTo: ({ queryParams }: { queryParams: Record<string, string> }) =>
                  queryParams['tipo'] === 'CAMBIOS'
                    ? conConsulta(`${RUTA_TRABAJADORES}/cambios-masivos/archivo`, { ...queryParams, tipo: null })
                    : conConsulta(`${RUTA_TRABAJADORES}/altas-archivo/subir`, queryParams),
              },
              { path: 'importaciones', pathMatch: 'full', redirectTo: aRuta(`${RUTA_TRABAJADORES}/altas-archivo`) },
              { path: 'importaciones/:id', redirectTo: conId(`${RUTA_TRABAJADORES}/altas-archivo`) },
              // Migración entre empresas pagadoras (2026-10-09, entrega D): también antes de `:relacion`.
              {
                path: 'migrar',
                loadComponent: () =>
                  import('./pages/migrar-trabajadores/migrar-trabajadores.page').then((m) => m.MigrarTrabajadoresPage),
              },
              {
                path: 'migraciones/:id',
                loadComponent: () =>
                  import('./pages/migracion/migracion.page').then((m) => m.MigracionPage),
              },
              {
                path: 'migraciones',
                loadComponent: () =>
                  import('./pages/migraciones/migraciones.page').then((m) => m.MigracionesPage),
              },
              // Altas que no procedieron (2026-10-09): las relaciones cuya alta se canceló. Antes de `:relacion`.
              {
                path: 'no-procedieron',
                loadComponent: () =>
                  import('./pages/altas-no-procedieron/altas-no-procedieron.page').then((m) => m.AltasNoProcedieronPage),
              },
              // Avisos al IMSS (fnomina 5f6bfe0, etapa IM1): la lista con los archivos del IDSE y el detalle; y las
              // modificaciones del bimestre (etapa IM2): el SBC que cambia sin movimiento. Antes de `:relacion`.
              // SINAY: sin `permisoGuard(P.IMSS_VER)` (D19): toda sesion los ve; generar archivos y anotar la
              // presentacion, el rechazo o la rectificacion (`IMSS_AVISOS` en fnomina) los habilita `trabajadores-capturar`.
              {
                path: 'avisos-imss',
                loadComponent: () => import('./pages/avisos-imss/avisos-imss.page').then((m) => m.AvisosImssPage),
              },
              {
                path: 'avisos-imss/:id',
                loadComponent: () => import('./pages/aviso-imss/aviso-imss.page').then((m) => m.AvisoImssPage),
              },
              {
                path: 'bimestre-imss',
                loadComponent: () => import('./pages/bimestre-imss/bimestre-imss.page').then((m) => m.BimestreImssPage),
              },
              // Bajas del personal (2026-10-20, etapa BJ1): la lista con su seguimiento y el detalle. Antes de `:relacion`.
              // SINAY: sin `permisoGuard(P.BAJAS_…)` ni `P.TRABAJADORES_…` (D19): toda sesion ve las bajas y su
              // descarga; aplicar, anotar, corregir y cancelar los habilita `trabajadores-capturar`, y las masivas,
              // ademas, `trabajadores-masivos`.
              {
                path: 'bajas',
                loadComponent: () => import('./pages/bajas/bajas.page').then((m) => m.BajasPage),
              },
              // Bajas masivas (2026-10-20, etapa BJ3): por selección, por archivo, su detalle y su lista. Antes de `bajas/:id`
              // (y la carga antes de `bajas/masiva/:id`). Hacer una pide BAJAS_APLICAR, TRABAJADORES_MASIVO y
              // TRABAJADORES_CAPTURAR; verlas, BAJAS_VER y TRABAJADORES_VER.
              {
                path: 'bajas/masiva',
                // Con algo elegido o capturado sin pedir la vista previa, pregunta antes de salir.
                canDeactivate: [preguntarAlSalir],
                loadComponent: () =>
                  import('./pages/baja-masiva-nueva/baja-masiva-nueva.page').then((m) => m.BajaMasivaNuevaPage),
              },
              {
                path: 'bajas/masiva/cargar',
                loadComponent: () => import('./pages/cargar-bajas/cargar-bajas.page').then((m) => m.CargarBajasPage),
              },
              {
                path: 'bajas/masiva/:id',
                loadComponent: () => import('./pages/baja-masiva/baja-masiva.page').then((m) => m.BajaMasivaPage),
              },
              {
                path: 'bajas/masivas',
                loadComponent: () => import('./pages/bajas-masivas/bajas-masivas.page').then((m) => m.BajasMasivasPage),
              },
              {
                path: 'bajas/:id',
                loadComponent: () => import('./pages/baja/baja.page').then((m) => m.BajaPage),
              },
              {
                path: ':relacion',
                loadComponent: () =>
                  import('./pages/ficha-trabajador/ficha-trabajador.page').then((m) => m.FichaTrabajadorPage),
              },
            ],
          },
        ],
      },
      // Las rutas de antes del padron (sin el prefijo de su area).
      { path: 'trabajadores', children: deAntes(RUTA_TRABAJADORES) },

      {
        // Nomina (2026-10-13, etapa I1): las incidencias del periodo, fuera de los espacios como el padron.
        // La API filtra por el alcance de cada usuario y revisa la captura sobre cada nomina.
        // SINAY: sin `permisoGuard(P.INCIDENCIAS_VER)` (D19): entra quien tiene el modulo `nomina` y cada pantalla
        // habilita la captura con `incidencias-capturar` en la nomina.
        path: 'nomina',
        canActivate: [moduloGuard('nomina')],
        children: [
          // La pagina del area (antes `/nomina` llevaba a «Incidencias»).
          { path: '', pathMatch: 'full', loadComponent: aterrizaje, data: { area: 'nomina' } },
          {
            // El calculo del periodo en operacion (etapa C1, 2026-10-15): calcular (en el fondo con 200 trabajadores
            // o mas), su avance, el resumen, excluir e incluir y calcular solo los cambios. `?nomina&cliente`.
            // SINAY: sin `permisoGuard(P.NOMINA_VER)` (D19): toda sesion ve; calcular, excluir, cerrar y reabrir los
            // habilita `incidencias-cargar` (y capturar) en la nomina.
            path: 'calcular',
            loadComponent: () => import('./pages/calcular/calcular.page').then((m) => m.CalcularPage),
          },
          {
            // La nomina calculada de un periodo: la sabana y el recibo de cada trabajador al lado.
            // `?nomina&cliente&periodo&trabajador`.
            path: 'calculada',
            loadComponent: () =>
              import('./pages/nomina-calculada/nomina-calculada.page').then((m) => m.NominaCalculadaPage),
          },
          {
            // La carga masiva va antes que la pantalla principal; ver una carga pide solo ver incidencias
            // (cargar, aplicar y cancelar los revisa la pantalla con la nomina).
            path: 'incidencias/carga',
            loadComponent: () =>
              import('./pages/carga-incidencias/carga-incidencias.page').then((m) => m.CargaIncidenciasPage),
          },
          {
            // La consulta de periodos anteriores: la misma pagina en modo consulta (sin captura).
            path: 'incidencias/anteriores',
            data: { modo: 'anteriores' },
            loadComponent: () => import('./pages/incidencias/incidencias.page').then((m) => m.IncidenciasPage),
          },
          {
            // El historico de faltas e incapacidades (2026-10-14): por mes o por fechas, de una nomina o de un
            // trabajador, por aplicacion o por fecha real. Solo consulta (y el folio de una incapacidad).
            path: 'faltas-incapacidades/historico',
            loadComponent: () =>
              import('./pages/historico-ausencias/historico-ausencias.page').then((m) => m.HistoricoAusenciasPage),
          },
          {
            // La consulta de periodos anteriores se retiro el 2026-10-14: lleva al historico con la misma nomina.
            path: 'faltas-incapacidades/anteriores',
            pathMatch: 'full',
            redirectTo: ({ queryParams }: { queryParams: Record<string, string> }) => {
              const nomina = Number(queryParams['nomina']);
              return Number.isInteger(nomina) && nomina > 0
                ? `/nomina/faltas-incapacidades/historico?nomina=${nomina}`
                : '/nomina/faltas-incapacidades/historico';
            },
          },
          {
            // Faltas e incapacidades (etapa I2; corregida el mismo dia): todo en el periodo en operacion de la
            // nomina; `?trabajador=` abre su calendario (desde la ficha). Si queda algo sin guardar, pregunta.
            path: 'faltas-incapacidades',
            canDeactivate: [preguntarAlSalir],
            loadComponent: () =>
              import('./pages/faltas-incapacidades/faltas-incapacidades.page').then((m) => m.FaltasIncapacidadesPage),
          },
          {
            // Deducciones fijas (2026-10-14, etapa I3): prestamos y deducciones por fechas de cada trabajador, que se
            // aplican solas en cada periodo, y la consulta de sus creditos y pension. Todo cambio lleva motivo.
            path: 'deducciones-fijas',
            canDeactivate: [preguntarAlSalir],
            loadComponent: () =>
              import('./pages/deducciones-fijas/deducciones-fijas.page').then((m) => m.DeduccionesFijasPage),
          },
          {
            path: 'incidencias',
            // Si algo no se pudo guardar (o lo pegado no se ha guardado), pregunta antes de salir.
            canDeactivate: [preguntarAlSalir],
            loadComponent: () => import('./pages/incidencias/incidencias.page').then((m) => m.IncidenciasPage),
          },
        ],
      },

      {
        // Reportes (2026-10-16): las descargas, fuera de los espacios como Nomina. La API filtra por el alcance de
        // cada usuario y revisa el permiso sobre cada nomina.
        path: 'reportes',
        canActivate: [moduloGuard('reportes')],
        children: [
          // La pagina del area (antes `/reportes` llevaba a la nomina calculada).
          { path: '', pathMatch: 'full', loadComponent: aterrizaje, data: { area: 'reportes' } },
          {
            // El Excel de la nomina calculada de un periodo (la elegida y su cadena de complementos): vista previa y
            // descarga. `?cliente&nomina&periodo`.
            // SINAY: sin `permisoGuard(P.NOMINA_DESCARGAR)` (D19): ver y descargar, toda sesion.
            path: 'nomina-calculada',
            loadComponent: () =>
              import('./pages/reporte-nomina-calculada/reporte-nomina-calculada.page').then(
                (m) => m.ReporteNominaCalculadaPage,
              ),
          },
        ],
      },

      // ── Empresas y clientes: empresas, clientes y todas las nominas, cada registro con sus pestañas ──
      {
        path: 'empresas-clientes',
        canActivate: [moduloGuard('empresas-clientes')],
        children: [
          { path: '', pathMatch: 'full', loadComponent: aterrizaje, data: { area: 'empresas-clientes' } },
          {
            path: 'empresas',
            loadComponent: espacio,
            data: { espacio: 'EMPRESAS' },
            children: [
              { path: '', loadComponent: empresas },
              {
                path: ':empresa',
                data: { nivel: 'EMPRESA' },
                children: [
                  { path: '', pathMatch: 'full', redirectTo: 'datos' },
                  { path: 'datos', data: { seccion: 'Datos de la empresa', vista: 'datos' }, loadComponent: empresas },
                  {
                    path: 'registros',
                    data: { seccion: 'Registros patronales', config: true },
                    loadComponent: () => import('./pages/registros/registros.page').then((m) => m.RegistrosPage),
                  },
                  {
                    path: 'estructura',
                    data: { seccion: 'Estructura', config: true },
                    loadComponent: () => import('./pages/estructura/estructura.page').then((m) => m.EstructuraPage),
                    // SINAY: `preguntarAlSalir` (tolera la pantalla sin crear) en lugar de `salirSinGuardar`.
                    canDeactivate: [preguntarAlSalir],
                  },
                  {
                    // Catalogos de la empresa para sus trabajadores (2026-10-08): los cambia quien cambia la estructura.
                    path: 'turnos',
                    data: { seccion: 'Turnos y sindicatos', config: true },
                    loadComponent: () =>
                      import('./pages/turnos-sindicatos/turnos-sindicatos.page').then((m) => m.TurnosSindicatosPage),
                  },
                  // Los trabajadores son de Personal (2026-10-12): la lista de su area con la empresa elegida en
                  // su filtro.
                  { path: 'trabajadores', redirectTo: conLaEmpresa(RUTA_TRABAJADORES) },
                  // La configuracion de la empresa esta en Configuracion.
                  ...configuracionDeAntes(),
                  {
                    path: 'clientes',
                    children: [
                      {
                        path: '',
                        data: { seccion: 'Clientes en esta empresa', vista: 'clientes' },
                        loadComponent: empresas,
                      },
                      {
                        path: ':cliente',
                        data: { nivel: 'CLIENTE_EMPRESA' },
                        children: [
                          { path: '', pathMatch: 'full', redirectTo: 'nominas' },
                          ...configuracionDeAntes(),
                          {
                            path: 'nominas',
                            children: [
                              // Las nominas del cliente en la empresa: alta y cambio.
                              { path: '', data: { seccion: 'Nóminas' }, loadComponent: nominasClienteEmpresa },
                              {
                                // La nomina ya no es un espacio: su configuracion y su calendario estan en
                                // Configuracion. `:tipo` es el id de la nomina.
                                path: ':tipo',
                                children: [
                                  { path: '', pathMatch: 'full', redirectTo: aConfiguracion('conceptos') },
                                  ...configuracionDeAntes(),
                                  { path: 'calendario', redirectTo: aConfiguracion('calendario') },
                                ],
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: 'clientes',
            loadComponent: espacio,
            data: { espacio: 'CLIENTES' },
            children: [
              { path: '', loadComponent: clientes },
              // Los grupos de clientes eran una pantalla de SINAY; desde el Bloque 2 son la ventana
              // «Grupos» de la lista (como en fnomina). Antes de `:cliente`, que si no lo tomaria como id.
              { path: 'grupos', redirectTo: RUTA_CLIENTES },
              {
                path: ':cliente',
                data: { nivel: 'CLIENTE' },
                children: [
                  { path: '', pathMatch: 'full', redirectTo: 'datos' },
                  { path: 'datos', data: { seccion: 'Datos del cliente', vista: 'datos' }, loadComponent: clientes },
                  {
                    path: 'razones-sociales',
                    data: { seccion: 'Razones sociales', vista: 'razones' },
                    loadComponent: clientes,
                  },
                  {
                    path: 'facturadoras',
                    data: { seccion: 'Empresas que le facturan', vista: 'facturadoras' },
                    loadComponent: clientes,
                  },
                  {
                    path: 'empresas',
                    data: { seccion: 'Empresas donde está su personal', vista: 'empresas' },
                    loadComponent: clientes,
                  },
                  // La configuracion del cliente esta en Configuracion.
                  ...configuracionDeAntes(),
                ],
              },
            ],
          },
          {
            // Todas las nominas del alcance (2026-10-09), en Empresas y clientes: consulta, con el paso a las
            // nominas de su cliente en su empresa. nominaapi filtra `GET /tipos-nomina` por el alcance.
            path: 'nominas',
            loadComponent: () => import('./pages/nominas/nominas.page').then((m) => m.NominasPage),
          },
        ],
      },
      // Las rutas de antes de Empresas y clientes (sin el prefijo de su area): la misma ruta debajo de el.
      { path: 'empresas', children: deAntes(RUTA_EMPRESAS) },
      { path: 'clientes', children: deAntes(RUTA_CLIENTES) },
      { path: 'nominas', children: deAntes(RUTA_NOMINAS) },
      // El directorio se quito (Juan, 2026-10-12: repetia las listas de Empresas y Clientes); un enlace
      // guardado lleva a Empresas.
      { path: 'directorio', redirectTo: RUTA_EMPRESAS },

      // ── Rutas de antes: llevan a su lugar en los espacios ──
      { path: 'conceptos', redirectTo: aRuta('/configuracion/conceptos') },
      { path: 'variables', redirectTo: aRuta('/configuracion/variables') },
      { path: 'bases', redirectTo: aRuta('/configuracion/bases') },
      { path: 'prestaciones', redirectTo: aRuta('/configuracion/prestaciones') },
      { path: 'esquemas', redirectTo: aRuta('/configuracion/esquemas') },
      { path: 'grupos', redirectTo: RUTA_CLIENTES },
      { path: 'calendario', redirectTo: aRuta('/configuracion/calendario') },
      { path: 'estructura', redirectTo: aLaEmpresa('estructura') },
      { path: 'registros-patronales', redirectTo: aLaEmpresa('registros') },
      // La consulta de los parametros se mudo a Configuracion (2026-10-22).
      { path: 'parametros', redirectTo: aRuta('/configuracion/consulta-parametros') },

      {
        // Pagina de referencia del catalogo (ui + theme). Fuera del menu.
        path: 'demo',
        loadComponent: () => import('./pages/demo/demo.page').then((m) => m.DemoPage),
      },
    ],
  },
  {
    // Lo que no existe vuelve a la primera pantalla del acceso (con guard, por eso no es `redirectTo`).
    path: '**',
    canActivate: [inicioGuard()],
    children: [],
  },
];
